import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type PointerEvent } from "react"
import { SingleClipPlayer, type TimelinePlayerPlaybackApi } from "@/components/timeline-player"

const NO_SUBSCRIBE = () => () => undefined
const ZERO = () => 0

/** Шаг подписей линейки: чтобы меток было не больше ~6 на любую длину ролика. */
function tickStepS(durationS: number): number {
  if (durationS <= 6) return 1
  if (durationS <= 15) return 2
  if (durationS <= 40) return 5
  if (durationS <= 120) return 15
  return 30
}

/** Откуда дорожка берёт время и куда перематывает: плеер страницы шота или пара роликов шторки. */
export type ScrubSource = {
  subscribe: (listener: () => void) => () => void
  timeMs: () => number
  scrubTo: (ms: number) => void
  endScrub: () => void
}

export function playbackScrubSource(api: TimelinePlayerPlaybackApi): ScrubSource {
  return {
    subscribe: (listener) => api.timeStore.subscribe(listener),
    timeMs: () => api.timeStore.get(),
    scrubTo: (ms) => api.controls.scrubTo(ms),
    endScrub: () => api.controls.endScrub(),
  }
}

/**
 * Минималистичная дорожка под видео панели просмотра: линейка секунд, прогресс и плейхед, без кадров на фоне.
 * Позиции — в процентах ширины, без масштаба под ширину: таймлайн плеера (TimelineLanes) подгоняет масштаб
 * ResizeObserver'ом и в узкой прокручиваемой панели давал горизонтальный скролл и дрожание.
 */
export function ReviewScrubber({ source, durationMs }: { source: ScrubSource | null; durationMs: number }) {
  const subscribe = useCallback((listener: () => void) => source?.subscribe(listener) ?? (() => undefined), [source])
  const getTime = useCallback(() => source?.timeMs() ?? 0, [source])
  const timeMs = useSyncExternalStore(source ? subscribe : NO_SUBSCRIBE, source ? getTime : ZERO)
  const trackRef = useRef<HTMLDivElement>(null)
  const scrubbing = useRef(false)

  const msAt = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect()
    if (!rect || rect.width <= 0) return 0
    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)) * durationMs
  }
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (!source) return
    scrubbing.current = true
    e.currentTarget.setPointerCapture(e.pointerId)
    source.scrubTo(msAt(e.clientX))
  }
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (scrubbing.current) source?.scrubTo(msAt(e.clientX))
  }
  const onPointerUp = () => {
    if (!scrubbing.current) return
    scrubbing.current = false
    source?.endScrub()
  }

  const durationS = durationMs / 1000
  const step = tickStepS(durationS)
  const ticks: number[] = []
  for (let s = 0; s <= durationS + 1e-6; s += step) ticks.push(s)
  const progress = durationMs > 0 ? Math.min(100, (timeMs / durationMs) * 100) : 0

  return (
    <div
      ref={trackRef}
      className="relative h-8 w-full cursor-pointer touch-none select-none"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {ticks.map((s) => (
        <span
          key={s}
          className="absolute top-0 text-[10px] text-ui-text-muted tabular-nums"
          style={{ left: `${(s / durationS) * 100}%`, transform: s === 0 ? undefined : "translateX(-50%)" }}
        >
          {s}
        </span>
      ))}
      <div className="absolute inset-x-0 top-5 h-1 rounded-full bg-ui-surface-raised" />
      <div className="absolute top-5 left-0 h-1 rounded-full bg-ui-accent" style={{ width: `${progress}%` }} />
      <div
        className="absolute top-3.5 h-4 w-0.5 -translate-x-1/2 rounded-full bg-ui-accent"
        style={{ left: `${progress}%` }}
      />
    </div>
  )
}

/**
 * Видео в панели просмотра: плеер страницы шота (видео, таймкод, транспорт) и минималистичная дорожка под ним.
 * Длительность — из версии, иначе из самого видео после загрузки метаданных.
 */
export function ReviewVideoPlayer({
  sourceId,
  streamUrl,
  durationMs,
  aspectRatio,
  onPlaybackReady,
}: {
  sourceId: number
  streamUrl: string
  durationMs?: number | null
  aspectRatio?: number | null
  onPlaybackReady?: (api: TimelinePlayerPlaybackApi | null) => void
}) {
  const [api, setApi] = useState<TimelinePlayerPlaybackApi | null>(null)
  const [metaMs, setMetaMs] = useState<number | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const handleReady = useCallback(
    (next: TimelinePlayerPlaybackApi | null) => {
      setApi(next)
      onPlaybackReady?.(next)
    },
    [onPlaybackReady],
  )

  useEffect(() => {
    if (durationMs) return
    const video = wrapperRef.current?.querySelector("video")
    if (!video) return
    const read = () => {
      if (Number.isFinite(video.duration) && video.duration > 0) setMetaMs(video.duration * 1000)
    }
    read()
    video.addEventListener("loadedmetadata", read)
    return () => video.removeEventListener("loadedmetadata", read)
  }, [durationMs, api])

  const total = durationMs || metaMs
  const source = useMemo(() => (api ? playbackScrubSource(api) : null), [api])
  return (
    <div ref={wrapperRef} className="flex flex-col gap-1">
      <SingleClipPlayer
        key={sourceId}
        sourceId={sourceId}
        streamUrl={streamUrl}
        durationMs={durationMs ?? null}
        aspectRatio={aspectRatio ?? null}
        autoPlay={false}
        defaultLoop
        showTrackHeaders={false}
        showTimeline={false}
        onPlaybackReady={handleReady}
        className="w-full max-w-full"
      />
      {total ? <ReviewScrubber source={source} durationMs={total} /> : null}
    </div>
  )
}
